/* STUB IMPLEMENTATION — see src/stubs/README.md.
 *
 * The cluster console's half. What it lets in follows from what the account
 * half chose, through src/stubs/stubState.ts — the same dependency the real
 * thing has, and the reason the demonstration is worth anything: choose an
 * identity provider in the account, come here, and this is what changed.
 *
 * The way in is played out rather than assumed. There is no identity provider
 * here, so this file plays one: starting a sign-in answers with an address that
 * returns to `/login` with a code and a state, the browser really does leave
 * and come back, and the console really does exchange them. What the pretend
 * provider answers — a member of the group, somebody in no group, an identity
 * that does not verify, silence — is a switch on the sign-in screen.
 *
 * Writing the catalogue is played out the same way, including the half that is
 * easy to get wrong: a declaration that stands while the cluster it was meant
 * for could not be told.
 */

import type { ClusterApi } from '../api/client';
import type {
  AuditEvent,
  AuditFilter,
  ClusterNode,
  ClusterSession,
  ClusterSignInStart,
  InstallKey,
  InstallRole,
  JoinToken,
  OfferedResource,
  Resource,
  ResourceInput,
  Scope,
  SignInCallback,
  SignInOffer,
} from '../api/types';
import { classWord } from '../api/types';
import { ApiError } from '../api/errors';
import { STUB_TENANCY } from './fixtures';
import {
  clearClusterSession,
  completeStubSignIn,
  readClusterSession,
  readIdentity,
  readResources,
  removeStubResource,
  removeStubScope,
  stubScopes,
  writeStubScope,
  startStubSignIn,
  writeStubResource,
} from './stubState';

/** What the stub's registry holds. Mutable: a revocation has to show. */
const nodes: ClusterNode[] = [
  {
    id: 'n-1',
    role: 'connector',
    common_name: 'gdsgate-connector',
    fingerprint: '0123456789abcdef',
    enrolled_at: 1_769_000_000,
    claimed_name: 'warehouse rack 4',
  },
  {
    id: 'n-2',
    role: 'proxy',
    common_name: 'gdsgate-proxy',
    fingerprint: 'fedcba9876543210',
    enrolled_at: 1_768_900_000,
  },
];

/**
 * What the connectors of the demonstration say they serve. One resource the
 * catalogue knows and one it does not — the second is the row a person has
 * something to do about.
 */
const offered: OfferedResource[] = [
  {
    node_id: 'n-1',
    claimed_name: 'warehouse rack 4',
    resource_id: 'db-1',
    kind: 'postgres',
    offered_at: 1_769_000_000,
    declared: true,
  },
  {
    node_id: 'n-1',
    claimed_name: 'warehouse rack 4',
    resource_id: 'prod-db',
    kind: 'postgres',
    offered_at: 1_769_000_000,
    declared: false,
  },
];

/**
 * A few links of a chain, of the shapes the screen has to render differently:
 * a refusal, a session that ran, an administrative change carrying a name the
 * control plane asserted, and a detail the page had to cut.
 *
 * **And of both epochs, in one chain, because that is what a chain is.** The
 * newest rows are what a current cluster seals — one action name for every
 * decision, the schema action and the session id as fields of a JSON detail, a
 * code beside the name — and the older ones are what the same tenancy wrote
 * before its upgrade and cannot ever rewrite: the schema action in the action
 * column, `key=value` text, no code at all. A demonstration built only from the
 * new shape would let the next reader assume the new shape is all there is,
 * which is the one assumption this screen cannot afford.
 */
const chain: AuditEvent[] = [
  // What an MCP server serves, held against what it served before. Its own
  // class, and the one row here that is neither a decision nor anything that
  // happened inside a session: a tool's description changed under a pin, which
  // is the shape of attack the class exists to make visible.
  {
    at: 1_769_000_940,
    class: 'ResourceIntegrity',
    action: 'mcp.descriptor_changed',
    actor: 'dana@acme.example',
    target: 'McpServer::"docs-mcp"',
    outcome: 'Failure',
    code: 'MCP-0301',
    detail: '{"resource_kind":"tool","tool":"search_docs"}',
    detail_truncated: false,
  },
  // A session on a current cluster, whole: the decision that let it open, the
  // open, a statement inside it, a refusal, and the close. Five rows that show
  // the panel what it now has to render — one action name across every
  // decision, told apart by `cedar_action`, and details that are objects.
  {
    at: 1_769_000_900,
    class: 'SessionActivity',
    action: 'session.close',
    actor: 'dana@acme.example',
    target: 'prod-db',
    outcome: 'Success',
    code: 'SES-0102',
    detail:
      '{"session_uid":"7a41c0","kind":"postgres","bytes":18432,' +
      '"recording":"recording://7a41c0","sha256":"9f2b1c…"}',
    detail_truncated: false,
  },
  {
    at: 1_769_000_860,
    class: 'Authorization',
    action: 'policy.decision',
    actor: 'dana@acme.example',
    target: 'prod-db',
    outcome: 'Deny',
    code: 'POL-0202',
    cedar_action: 'dbQuery',
    detail:
      '{"cedar_action":"dbQuery","session_uid":"7a41c0","reason":"capability_not_granted",' +
      '"category":"write","deciding":["p-db-readonly"]}',
    detail_truncated: false,
  },
  {
    at: 1_769_000_820,
    class: 'SessionActivity',
    action: 'db.query',
    actor: 'dana@acme.example',
    target: 'prod-db',
    outcome: 'Success',
    code: 'SES-0301',
    detail:
      '{"kind":"postgres","query":"SELECT id, email FROM customers WHERE created_at > now() - interval \'7 days\'",' +
      '"params":[],"category":"read","session_uid":"7a41c0"}',
    detail_truncated: false,
  },
  {
    at: 1_769_000_800,
    class: 'SessionActivity',
    action: 'session.open',
    actor: 'dana@acme.example',
    target: 'prod-db',
    outcome: 'Success',
    code: 'SES-0101',
    detail:
      '{"session_uid":"7a41c0","kind":"postgres","session_role":"readonly",' +
      '"record_request":"full","src_ip":"203.0.113.4"}',
    detail_truncated: false,
  },
  {
    at: 1_769_000_780,
    class: 'Authorization',
    action: 'policy.decision',
    actor: 'dana@acme.example',
    target: 'prod-db',
    outcome: 'Allow',
    code: 'POL-0101',
    cedar_action: 'connect',
    detail:
      '{"cedar_action":"connect","lane":"db","deciding":["p-db-readonly"],"src_ip":"203.0.113.4"}',
    detail_truncated: false,
  },
  // Everything below is the same tenancy before its upgrade. No codes, and a
  // decision's schema action stands in the action column — which is where the
  // control plane takes `cedar_action` from for these, so the panel reads one
  // field for both epochs and never asks which epoch a row is from.
  {
    at: 1_769_000_400,
    class: 'Authorization',
    action: 'connect',
    actor: 'sam@acme.example',
    target: 'srv1',
    outcome: 'Deny',
    cedar_action: 'connect',
    detail: 'reason=no_rule src_ip=203.0.113.9',
    detail_truncated: false,
  },
  {
    at: 1_769_000_300,
    class: 'SessionActivity',
    action: 'ssh.session.close',
    actor: 'dana@acme.example',
    target: 'srv1',
    outcome: 'Success',
    detail: 'session_uid=0f2c bytes=18432 recording=recording://0f2c',
    detail_truncated: false,
  },
  // The rest of that session, so the panel has a timeline to gather rather than
  // a single row it cannot draw one from.
  {
    at: 1_769_000_260,
    class: 'SessionActivity',
    action: 'ssh.exec',
    actor: 'dana@acme.example',
    target: 'srv1',
    outcome: 'Allow',
    detail: 'session_uid=0f2c cmd=systemctl status nginx',
    detail_truncated: false,
  },
  {
    at: 1_769_000_240,
    class: 'Authorization',
    action: 'connect',
    actor: 'dana@acme.example',
    target: 'srv1',
    outcome: 'Allow',
    cedar_action: 'connect',
    detail: 'session_uid=0f2c src_ip=203.0.113.4 mfa=passkey',
    detail_truncated: false,
  },
  // A program acting under somebody's delegation, and a machine identity: the
  // two the actor column has to mark, because reading either as a person is the
  // mistake worth preventing.
  {
    at: 1_769_000_180,
    class: 'Authorization',
    action: 'dbQuery',
    actor: 'spiffe://gdsgate/user/dana/delegation/d-7f2a',
    target: 'prod-db',
    outcome: 'Allow',
    cedar_action: 'dbQuery',
    detail: 'session_uid=b41e rows=12 statement=select',
    detail_truncated: false,
  },
  {
    at: 1_769_000_150,
    class: 'Authorization',
    action: 'dbQuery',
    actor: 'spiffe://gdsgate/user/dana/delegation/d-7f2a',
    target: 'prod-db',
    outcome: 'Deny',
    cedar_action: 'dbQuery',
    detail: 'session_uid=b41e reason=capability_not_granted statement=update',
    detail_truncated: false,
  },
  {
    at: 1_769_000_120,
    class: 'SessionActivity',
    action: 'tcp.session.open',
    actor: 'spiffe://gdsgate/svc/billing',
    target: 'ledger',
    outcome: 'Success',
    detail: 'session_uid=c902 src_ip=10.0.0.31',
    detail_truncated: false,
  },
  {
    at: 1_769_000_200,
    class: 'AdminAction',
    action: 'manageCatalog',
    actor: 'gdsgate-provisioner',
    target: 'SshHost::"srv1"',
    outcome: 'Allow',
    detail: 'scope=upsert src_ip=10.0.0.4',
    claimed_operator: 'kate@acme.example',
    detail_truncated: false,
  },
  {
    at: 1_769_000_100,
    class: 'AdminAction',
    action: 'viewAudit',
    actor: 'dana@acme.example',
    target: 'AuditLog::"chain"',
    outcome: 'Allow',
    detail: 'page_size=100 src_ip=203.0.113.4',
    detail_truncated: true,
  },
];

/** The account console, wherever the demonstration is being served from. */
const ACCOUNT_URL = `${window.location.origin}/?entry=account`;

/** What the tenancy would have to register at its provider. */
const REDIRECT_URI = `https://${STUB_TENANCY.tenancy_domain}/login`;

/** How long a started sign-in stays good for. The server's own default. */
const SIGN_IN_SECONDS = 600;

function delay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 200 + Math.random() * 300));
}

function offer(): SignInOffer {
  const identity = readIdentity();
  const status = identity.in_flight
    ? 'reconfiguring'
    : identity.applied === undefined
      ? 'not_configured'
      : 'ready';

  const answer: SignInOffer = {
    status,
    since: identity.changed_at,
    account_url: ACCOUNT_URL,
    tenancy: STUB_TENANCY,
    redirect_uri: REDIRECT_URI,
  };
  if (identity.applied !== undefined) answer.provider = identity.applied;
  return answer;
}

export function createStubClusterApi(): ClusterApi {
  return {
    async getSession() {
      await delay();
      return readClusterSession();
    },

    async signInOffer() {
      await delay();
      return offer();
    },

    async startSignIn(): Promise<ClusterSignInStart> {
      await delay();

      const identity = readIdentity();
      if (identity.in_flight || identity.applied === undefined) {
        throw new ApiError('sign_in_unavailable', {
          status: 409,
          message:
            identity.in_flight
              ? 'this tenancy is moving to another identity provider'
              : 'no identity provider has been applied to this cluster',
        });
      }

      const { state } = startStubSignIn();
      // The pretend provider: it asks nothing and returns straight away. The
      // address is this same console's `/login`, which is what a real provider
      // is given as the return address too.
      const back = new URL('/login', window.location.origin);
      back.searchParams.set('code', 'stub-authorization-code');
      back.searchParams.set('state', state);

      return {
        authorization_url: back.toString(),
        redirect_uri: REDIRECT_URI,
        expires_at: new Date(Date.now() + SIGN_IN_SECONDS * 1000).toISOString(),
      };
    },

    async completeSignIn(callback: SignInCallback): Promise<ClusterSession> {
      await delay();
      return completeStubSignIn(callback.state);
    },

    async signOut() {
      await delay();
      clearClusterSession();
    },

    async listResources(): Promise<Resource[]> {
      await delay();
      return readResources().map((resource) => ({ ...resource }));
    },

    async putResource(id: string, input: ResourceInput) {
      await delay();
      writeStubResource(id, input);
    },

    async listServing(): Promise<string[]> {
      await delay();
      // One of the declared resources, so the catalogue shows both states: a
      // row with a tunnel behind it and a row with none.
      return readResources()
        .slice(0, 1)
        .map((resource) => resource.id ?? '');
    },

    async listNodes(): Promise<ClusterNode[]> {
      await delay();
      return nodes.map((node) => ({ ...node }));
    },

    async listOffered(): Promise<OfferedResource[]> {
      await delay();
      // Marked against the catalogue as it stands right now, so declaring the
      // offered resource in this demonstration makes its row leave the section
      // — which is the behaviour the screen is built on.
      const declared = new Set(readResources().map((resource) => resource.id));
      return offered.map((offer) => ({ ...offer, declared: declared.has(offer.resource_id) }));
    },

    async listAudit(filter: AuditFilter = {}): Promise<AuditEvent[]> {
      await delay();
      // Filtered here rather than answered whole, so the demonstration's
      // filters do something and a screen built on them can be seen working.
      const wanted = chain.filter(
        (row) =>
          // Against the class the row was **sealed** under, which is what a
          // cluster matches too. On a chain that spans the split of
          // `Authorization` that is the whole difference: the older rows answer
          // to the class they carry, not to the one the same event would get
          // today.
          (!filter.class || classWord(row.class) === filter.class) &&
          (!filter.actor || row.actor === filter.actor) &&
          (!filter.action || row.action.startsWith(filter.action)) &&
          (!filter.target || row.target === filter.target),
      );
      return wanted.slice(0, filter.limit ?? 100).map((row) => ({ ...row }));
    },

    async mintJoinToken(ttlSecs?: number): Promise<JoinToken> {
      await delay();
      // Drawn rather than fixed, so a screen saying "this is the only copy" is
      // saying it about something that really was different last time.
      const token = `stub-${Math.random().toString(36).slice(2, 10)}`;
      return { token, ttl_secs: ttlSecs ?? 600, role: 'connector' };
    },

    async mintInstallKey(role: InstallRole): Promise<InstallKey> {
      await delay();
      // Drawn, and shaped like the real one — Crockford's base32 after the
      // prefix — so a screen showing it is showing what a person will see.
      const body = Array.from(
        { length: 26 },
        () => '0123456789ABCDEFGHJKMNPQRSTVWXYZ'[Math.floor(Math.random() * 32)],
      ).join('');
      const key = `gate-olk-${body}`;
      return {
        command: `curl -fsSL https://acme-app.gate.example.com/install.sh | sh -s -- ${key}`,
        key,
        role,
        expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
      };
    },

    async deleteNode(id: string) {
      await delay();
      const found = nodes.find((node) => node.id === id);
      if (found === undefined) {
        throw new ApiError('tenant_unreachable', { status: 502, message: 'unknown' });
      }
      if (found.revoked_at === undefined) {
        throw new ApiError('tenant_unreachable', {
          status: 502,
          message: 'not revoked — revoke it first',
        });
      }
      // A week, the cluster's own grace. Demonstrated rather than skipped: the
      // refusal is the one a person meets most, because the row they want gone
      // is usually the one they just revoked.
      const week = 7 * 24 * 60 * 60;
      if (Date.now() / 1000 - found.revoked_at < week) {
        throw new ApiError('tenant_unreachable', {
          status: 502,
          message: `too recent — this row may be removed from ${String(found.revoked_at + week)}`,
        });
      }
      nodes.splice(nodes.indexOf(found), 1);
    },

    async revokeNode(id: string) {
      await delay();
      const found = nodes.find((node) => node.id === id);
      if (!found) {
        throw new ApiError('cluster_refused', {
          status: 502,
          message: `this tenancy's cluster refused: no such node \`${id}\``,
        });
      }
      found.revoked_at = Math.floor(Date.now() / 1000);
    },

    async deleteResource(id: string) {
      await delay();
      removeStubResource(id);
    },

    async listScopes() {
      await delay();
      return stubScopes();
    },

    async putScope(prefix: string, input: Scope) {
      await delay();
      writeStubScope(prefix, input);
    },

    async deleteScope(prefix: string) {
      await delay();
      removeStubScope(prefix);
    },
  };
}
